import type { PagedResultDto } from "../dto/pagedResultDto"
import http from "../httpService"
import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"
import type {
  IIntegrationExecution,
  IIntegrationExecutionDetail,
  IIntegrationExecutionFilter,
  IRetryHistory,
  IDashboardSummary,
  IDailyExecutionMetric,
  IEntityTypeBreakdown,
  IRecentFailure,
  IHealthStatus,
  IDashboardFilter,
  IRetryPolicy,
  IAlertConfiguration,
  IHealthCheckConfig,
  IBulkRetryInput,
  IDismissDlqInput,
  IAlertRecipient,
  IEmailTemplate,
} from "@models/integrationHub/integrationHubModel"

class IntegrationHubService {
  // === Execution ===

  public async getAll(
    params: IIntegrationExecutionFilter
  ): Promise<PagedResultDto<IIntegrationExecution>> {
    const res = await http.get("api/services/app/IntegrationHub/GetAll", { params })
    return res.data.result
  }

  public async getDetail(id: number): Promise<IIntegrationExecutionDetail> {
    const res = await http.get("api/services/app/IntegrationHub/GetDetail", { params: { id } })
    return res.data.result
  }

  public async getRetryHistory(executionId: number): Promise<IRetryHistory[]> {
    const res = await http.get("api/services/app/IntegrationHub/GetRetryHistory", {
      params: { executionId },
    })
    return res.data.result
  }

  public async getByFlowId(flowId: string): Promise<IIntegrationExecution[]> {
    const res = await http.get("api/services/app/IntegrationHub/GetByFlowId", {
      params: { flowId },
    })
    return res.data.result
  }

  // === Retry Actions ===

  public async retry(executionId: number): Promise<void> {
    await http.post("api/services/app/IntegrationHub/Retry", { executionId })
  }

  public async bulkRetry(input: IBulkRetryInput): Promise<void> {
    await http.post("api/services/app/IntegrationHub/BulkRetry", input)
  }

  public async dismissDlq(input: IDismissDlqInput): Promise<void> {
    await http.post("api/services/app/IntegrationHub/DismissDlq", input)
  }

  // === Dashboard ===

  public async getDashboardSummary(params?: IDashboardFilter): Promise<IDashboardSummary> {
    const res = await http.get("api/services/app/IntegrationDashboard/GetSummary", { params })
    return res.data.result
  }

  public async getTimeSeries(params?: IDashboardFilter): Promise<IDailyExecutionMetric[]> {
    const res = await http.get("api/services/app/IntegrationDashboard/GetTimeSeries", { params })
    return res.data.result
  }

  public async getEntityTypeBreakdown(params?: IDashboardFilter): Promise<IEntityTypeBreakdown[]> {
    const res = await http.get("api/services/app/IntegrationDashboard/GetEntityTypeBreakdown", {
      params,
    })
    return res.data.result
  }

  public async getRecentFailures(count = 5): Promise<IRecentFailure[]> {
    const res = await http.get("api/services/app/IntegrationDashboard/GetRecentFailures", {
      params: { count },
    })
    return res.data.result
  }

  public async getHealthStatus(): Promise<IHealthStatus> {
    const res = await http.get("api/services/app/IntegrationDashboard/GetHealthStatus")
    return res.data.result
  }

  // === Configuration ===

  public async getRetryPolicy(): Promise<IRetryPolicy> {
    const res = await http.get("api/services/app/IntegrationConfig/GetRetryPolicy")
    return res.data.result
  }

  public async updateRetryPolicy(input: Partial<IRetryPolicy>): Promise<void> {
    await http.put("api/services/app/IntegrationConfig/UpdateRetryPolicy", input)
  }

  public async getAlertConfiguration(): Promise<IAlertConfiguration> {
    const res = await http.get("api/services/app/IntegrationConfig/GetAlertConfiguration")
    return res.data.result
  }

  public async updateAlertConfiguration(input: Partial<IAlertConfiguration>): Promise<void> {
    await http.put("api/services/app/IntegrationConfig/UpdateAlertConfiguration", input)
  }

  public async getHealthCheckConfig(): Promise<IHealthCheckConfig> {
    const res = await http.get("api/services/app/IntegrationConfig/GetHealthCheckConfig")
    return res.data.result
  }

  public async updateHealthCheckConfig(input: Partial<IHealthCheckConfig>): Promise<void> {
    await http.put("api/services/app/IntegrationConfig/UpdateHealthCheckConfig", input)
  }

  // === Alert Recipients ===

  public async getAlertRecipients(): Promise<IAlertRecipient[]> {
    const res = await http.get("api/services/app/IntegrationConfig/GetAlertRecipients")
    return res.data.result
  }

  public async addAlertRecipient(userId: number): Promise<void> {
    // Backend nhận primitive `long userId` → phải gửi qua query string, không đưa vào body
    await http.post("api/services/app/IntegrationConfig/AddAlertRecipient", undefined, {
      params: { userId },
    })
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
  }

  public async removeAlertRecipient(userId: number): Promise<void> {
    await http.delete("api/services/app/IntegrationConfig/RemoveAlertRecipient", {
      params: { userId },
    })
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
  }

  // === Email Template ===

  public async getEmailTemplate(): Promise<IEmailTemplate> {
    const res = await http.get("api/services/app/IntegrationConfig/GetEmailTemplate")
    return res.data.result
  }

  public async generateDefaultTemplate(): Promise<string> {
    const res = await http.post("api/services/app/IntegrationConfig/GenerateDefaultTemplate")
    return res.data.result
  }

  // Toast do AlertConfigForm phát sau khi lưu xong cả cấu hình + template (tránh 2 popup)
  public async updateEmailTemplate(input: { subject: string; body: string }): Promise<void> {
    await http.put("api/services/app/IntegrationConfig/UpdateEmailTemplate", input)
  }

  public async previewEmailTemplate(): Promise<string> {
    const res = await http.post("api/services/app/IntegrationConfig/PreviewEmailTemplate")
    return res.data.result
  }
}

export default new IntegrationHubService()
